/**
 * Line item state helpers for the Smart Line Item feature.
 *
 * Provides a single factory for creating line items (both Product and Custom mode),
 * normalization for loading saved jobs, and stripping client-only fields before API calls.
 */

/**
 * Quoted-price ("material price override") bases.
 *
 * One list, shared by the reducer's validation, the line editor's picker and
 * the cost trace's wording, so a basis can never be storable-but-unreadable.
 * The keys are the backend contract — do not rename one without the engine.
 *
 *   per_unit    amount × quantity            (Each)
 *   per_line    lump sum for the whole line  (Line total)
 *   per_sqft    amount × the line's sq ft    (Sq Ft)
 *   per_lin_ft  amount × lineal feet of run  (Lin Ft)
 *   per_board / per_gallon / per_lb          amount × quantity, where the
 *                                            quantity counts that unit
 */
export const MATERIAL_PRICE_OVERRIDE_BASES = [
  "per_unit",
  "per_line",
  "per_sqft",
  "per_lin_ft",
  "per_board",
  "per_gallon",
  "per_lb",
];

export const MATERIAL_PRICE_OVERRIDE_BASIS_LABELS = {
  per_unit: "Each",
  per_line: "Line total",
  per_sqft: "Sq Ft",
  per_lin_ft: "Lin Ft",
  per_board: "Board",
  per_gallon: "Gallon",
  per_lb: "Lb",
};

export const MATERIAL_PRICE_OVERRIDE_BASIS_OPTIONS =
  MATERIAL_PRICE_OVERRIDE_BASES.map((value) => ({
    value,
    label: MATERIAL_PRICE_OVERRIDE_BASIS_LABELS[value],
  }));

const LABOR_HOUR_FIELDS = [
  "cnc_hours",
  "cut_hours",
  "fab_hours",
  "finish_hours",
];

const isBlankOrZero = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Number.isFinite(Number(value)) && Number(value) === 0);

/**
 * Before the explicit confirmation flag existed, clearing an hour input wrote
 * literal zeroes but the engine still used the rate card. Keep those saved
 * estimates on that historical price while preserving new explicit zeroes.
 */
const hasLegacyUnconfirmedZeroLabor = (item) =>
  !Object.prototype.hasOwnProperty.call(item, "zero_labor_confirmed") &&
  String(item?.labor_source || "").toLowerCase() === "manual" &&
  LABOR_HOUR_FIELDS.every((field) => isBlankOrZero(item?.[field]));

const clearedLaborOverride = () => ({
  labor_source: null,
  ...Object.fromEntries(LABOR_HOUR_FIELDS.map((field) => [field, ""])),
});

/**
 * Create a blank material component for Custom Mode.
 */
export function createMaterialComponent(overrides = {}) {
  return {
    type: "material",
    name: "",
    material_id: null,
    dimensions: null,
    quantity_per_unit: 1,
    unit_cost: 0,
    unit: "each",
    rip_cut: null,
    ...overrides,
  };
}

/**
 * Create a blank operation component for Custom Mode. Defaults to a
 * generic "Labor" name because the overwhelming majority of ad-hoc
 * operations added to an assembly are generic labor line items (most
 * commonly fab hours) — the estimator can overwrite it when they know
 * the specific op ("CNC Profile", "Laminate", etc.). Empty default
 * caused users to stare at an empty field wondering what to type.
 */
export function createOperationComponent(overrides = {}) {
  return {
    type: "operation",
    name: "Labor",
    hours_per_unit: 0,
    department: "fab",
    ...overrides,
  };
}

/**
 * Create a blank hardware component for Custom Mode.
 */
export function createHardwareComponent(overrides = {}) {
  return {
    type: "hardware",
    name: "",
    unit_cost: 0,
    quantity_per_unit: 1,
    ...overrides,
  };
}

/**
 * Create a blank child line item for a typed (Product-mode) parent.
 *
 * A child is a line-shaped object restricted to product-mode fields — it can
 * never itself have `children`, `assembly_components`, `ai_drafted` or a
 * `mode`, so nesting depth is 1 by construction and there is no way to build
 * a grandchild.
 */
export function createChildLineItem(overrides = {}) {
  return {
    product_type_key: null,
    name: "",
    description: "",
    length: 0,
    width: 0,
    height: 0,
    quantity: 1,
    material_classification: "",
    material_id: null,
    finish_mode: null,
    waste_mode: null,
    waste_custom_pct: null,
    labor_source: null,
    cnc_hours: 0,
    cut_hours: 0,
    fab_hours: 0,
    finish_hours: 0,
    zero_labor_confirmed: false,
    material_price_override: null,
    material_price_override_basis: "per_unit",
    material_price_override_note: "",
    ...overrides,
  };
}

/**
 * Normalize a saved child line item so old drafts/JobVersions load cleanly.
 * Drops any `children` a payload might carry — grandchildren are not a shape
 * this app supports, so they are silently discarded rather than round-tripped.
 */
export function normalizeChildLineItem(child) {
  if (!child || typeof child !== "object") return createChildLineItem();
  const { children: _dropped, ...rest } = child;
  const clearsLegacyLabor = hasLegacyUnconfirmedZeroLabor(child);
  return {
    ...createChildLineItem(),
    ...rest,
    ...(clearsLegacyLabor ? clearedLaborOverride() : {}),
  };
}

/**
 * Create a new line item with explicit defaults.
 * Used by initialState and ADD_LINE_ITEM to stay in sync.
 */
export function createLineItem(overrides = {}) {
  return {
    // -- Smart Line Item fields --
    mode: "product",
    assembly_components: [],
    manual_total_override: null,
    name: "",
    description: "",
    children: [],

    // -- Product Mode fields --
    product_id: null,
    // Typed line: prices through the rate-card engine by product type instead
    // of a catalog product. Mutually exclusive with product_id. Default null so
    // old drafts/JobVersions (which never had this field) load cleanly.
    product_type_key: null,
    material_id: null,
    material_option_key: null,
    material_option_label: null,
    material_classification: "",
    material_selection_mode: "product_default",
    finish_readiness: "",
    // -- Typed-line finish + waste (rate-card engine) --
    // Default null so old drafts/JobVersions (which never had these) load
    // cleanly. finish_mode replaces finish_readiness on typed lines; waste
    // fields only apply to types whose waste_prompt_mode === 'prompt'.
    finish_mode: null,
    waste_mode: null,
    waste_custom_pct: null,
    // -- Typed-line material price override ("Quoted price") --
    // A quoted, lump-sum or specialty price for this line's material. Default
    // null so old drafts/JobVersions load cleanly and the reducer's clearing
    // rule (amount cleared -> basis + note cleared) has a stable key to reset.
    material_price_override: null,
    material_price_override_basis: "per_unit",
    material_price_override_note: "",
    length: 0,
    width: 0,
    height: 0,
    reinforcement_needed: false,
    additional_description: "",
    additional_units: 0,
    additional_cost_per_unit: 0,
    quantity: 1,
    unit_cost: 0,

    // -- Provenance --
    // Set only on a line the AI composer drafted: { draft_id, model,
    // generated_at, manufacturing_version, manufacturing_sha256, accepted_at }.
    // Default null so old drafts/JobVersions (which never had this field) load
    // cleanly, and so `normalizeLineItem` has a key to preserve.
    ai_drafted: null,

    // -- Labor fields --
    cnc_hours: 0,
    cut_hours: 0,
    fab_hours: 0,
    finish_hours: 0,
    // An all-zero labor override prices the line with no shop labor at all, so
    // it is warned about everywhere until an estimator says they meant it. The
    // flag is presentational — it rides along in the payload and the saved
    // JobVersion so the confirmation survives a reload, and touches no number.
    zero_labor_confirmed: false,
    fab_labor: {
      description: "TBD",
      cnc_hours: 0,
      cut_hours: 0,
      fab_hours: 0,
      paint_hours: 0,
    },
    ...overrides,
  };
}

/**
 * Normalize a saved line item (from JobVersion JSON) to ensure all expected
 * fields exist. Handles backward compatibility — old jobs without `mode` or
 * `assembly_components` get defaults applied.
 */
export function normalizeLineItem(item) {
  if (!item || typeof item !== "object") return createLineItem();
  const clearsLegacyLabor = hasLegacyUnconfirmedZeroLabor(item);

  return {
    ...createLineItem(),
    ...item,
    ...(clearsLegacyLabor ? clearedLaborOverride() : {}),
    // Ensure mode defaults to "product" for old data
    mode: item.mode || "product",
    // Ensure assembly_components is always an array
    assembly_components: Array.isArray(item.assembly_components)
      ? item.assembly_components
      : [],
    // Ensure description is always a string, and children is always an array
    // of normalized child line items — old jobs saved before Goal 4 carry
    // neither field.
    description: typeof item.description === "string" ? item.description : "",
    children: Array.isArray(item.children)
      ? item.children.map((child) => normalizeChildLineItem(child))
      : [],
    // Provenance survives the save/load round-trip: a line the AI drafted stays
    // badged forever. Anything that isn't an object is not provenance.
    ai_drafted:
      item.ai_drafted && typeof item.ai_drafted === "object"
        ? item.ai_drafted
        : null,
  };
}

/**
 * Strip client-only fields before sending line items to the API.
 * The backend doesn't need UI state like `fab_labor` display objects.
 *
 * `ai_drafted` is deliberately NOT stripped. It rides into /api/calculate and
 * into the saved JobVersion so an accepted AI draft stays historically badged —
 * "a model proposed these inputs" is part of the record, not UI chrome.
 *
 * `fab_labor` is stripped from the line AND from every child — a child is a
 * line-shaped object and carries the same UI-only display field once labor
 * fields are edited on it.
 */
export function stripLineItemClientFields(item) {
  const { fab_labor, children, ...rest } = item;
  return {
    ...rest,
    ...(Array.isArray(children)
      ? {
          children: children.map((child) => {
            const { fab_labor: _childFabLabor, ...childRest } = child || {};
            return childRest;
          }),
        }
      : {}),
  };
}

/**
 * Deep-clone a line item for duplication. Produces a new object
 * with new array references so mutations don't affect the source.
 */
export function duplicateLineItem(item) {
  return {
    ...item,
    assembly_components: item.assembly_components
      ? item.assembly_components.map((comp) => ({ ...comp }))
      : [],
    children: Array.isArray(item.children)
      ? item.children.map((child) => ({ ...child }))
      : [],
    fab_labor: item.fab_labor ? { ...item.fab_labor } : undefined,
    // A duplicate of a drafted line is still a drafted line — copy the stamp
    // rather than sharing the object, so accepting one copy can't stamp both.
    ai_drafted:
      item.ai_drafted && typeof item.ai_drafted === "object"
        ? { ...item.ai_drafted }
        : (item.ai_drafted ?? null),
  };
}

/**
 * True for a line the estimator has not touched: a fresh Product-mode row with
 * nothing chosen, nothing measured and no provenance. Landing an AI draft into
 * a job that holds exactly one of these replaces it, so a new estimate doesn't
 * carry a stray empty row next to the draft.
 */
export function isBlankLineItem(item) {
  if (!item || typeof item !== "object") return false;
  if ((item.mode || "product") !== "product") return false;
  if (item.ai_drafted) return false;
  if (item.product_type_key || item.product_id) return false;
  if (String(item.name || "").trim()) return false;
  if (String(item.description || "").trim()) return false;
  if ((item.assembly_components || []).length) return false;
  if ((item.children || []).length) return false;
  if (String(item.material_classification || "").trim()) return false;
  return ["length", "width", "height"].every((key) => {
    const value = item[key];
    return (
      value === "" || value === null || value === undefined || +value === 0
    );
  });
}
